import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

interface FloatingButtonsSettings {
  phone: string;
  whatsapp_number: string;
  show_floating_buttons: boolean;
  floating_button_position: 'left' | 'right';
  floating_button_visibility: 'web' | 'mobile' | 'both';
  floating_button_order: 'whatsapp-phone' | 'phone-whatsapp';
}

export default function FloatingButtons() {
  const location = useLocation();
  const [settings, setSettings] = useState<FloatingButtonsSettings | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  // Admin sayfalarında gösterme
  const isAdminPage = location.pathname.startsWith('/admin');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    const { data } = await supabase
      .from('theme_settings')
      .select('phone, whatsapp_number, show_floating_buttons, floating_button_position, floating_button_visibility, floating_button_order')
      .single();

    if (data) {
      setSettings(data as FloatingButtonsSettings);
    }
  };

  useEffect(() => {
    if (!settings?.show_floating_buttons || isAdminPage) {
      setIsVisible(false);
      return;
    }

    const checkVisibility = () => {
      const isMobile = window.innerWidth < 768;
      
      if (settings.floating_button_visibility === 'both') {
        setIsVisible(true);
      } else if (settings.floating_button_visibility === 'web' && !isMobile) {
        setIsVisible(true);
      } else if (settings.floating_button_visibility === 'mobile' && isMobile) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    checkVisibility();
    window.addEventListener('resize', checkVisibility);
    return () => window.removeEventListener('resize', checkVisibility);
  }, [settings, isAdminPage]);

  if (!settings || !isVisible || isAdminPage) return null;

  const handleWhatsAppClick = () => {
    if (!settings.whatsapp_number) return;
    const cleanNumber = settings.whatsapp_number.replace(/\s/g, '');
    window.open(`https://wa.me/${cleanNumber}`, '_blank');
  };

  const handlePhoneClick = () => {
    if (!settings.phone) return;
    window.location.href = `tel:${settings.phone}`;
  };

  const buttons = settings.floating_button_order === 'whatsapp-phone' 
    ? [
        { icon: 'ri-whatsapp-line', bg: 'bg-green-500', onClick: handleWhatsAppClick, label: 'WhatsApp', show: !!settings.whatsapp_number },
        { icon: 'ri-phone-line', bg: 'bg-blue-500', onClick: handlePhoneClick, label: 'Telefon', show: !!settings.phone }
      ]
    : [
        { icon: 'ri-phone-line', bg: 'bg-blue-500', onClick: handlePhoneClick, label: 'Telefon', show: !!settings.phone },
        { icon: 'ri-whatsapp-line', bg: 'bg-green-500', onClick: handleWhatsAppClick, label: 'WhatsApp', show: !!settings.whatsapp_number }
      ];

  return (
    <div
      className={`fixed bottom-6 z-50 flex gap-3 ${
        settings.floating_button_position === 'left' ? 'left-6' : 'right-6'
      }`}
    >
      {buttons.filter(btn => btn.show).map((button, index) => (
        <button
          key={index}
          onClick={button.onClick}
          className={`${button.bg} w-14 h-14 rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform cursor-pointer group relative`}
          aria-label={button.label}
        >
          <i className={`${button.icon} text-white text-2xl`}></i>
          <span className="absolute -top-10 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-3 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
            {button.label}
          </span>
        </button>
      ))}
    </div>
  );
}
